import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Stack,
  Avatar,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { Check as CheckIcon } from '@mui/icons-material';
import { useAppContext } from '../context/AppContext';
import hardwareIcon from '../assets/icon_hardware.png'; // Stellen Sie sicher, dass dieser Pfad korrekt ist
import './InfoboxSchutz.css';
import { selectCalculatedPremium } from '../context/util/selectors';
import { formatCurrency } from '../util/helper';

function InfoboxSchutz() {
  const theme = useTheme();
  const isTabletOrMobile = useMediaQuery(theme.breakpoints.down('lg'));

  const { state } = useAppContext();
  const versicherungssumme = formatCurrency(state.formData?.versicherungssumme);
  const paymentPeriod =
    state.availablePaymentPeriods?.find(paymentPeriod => paymentPeriod.code === state.formData?.zahlungsintervall)
      ?.label || 'monatlich';

  const calculatedPremium = selectCalculatedPremium(state);
  const selectedOptions =
    state.selectedProduct?.options
      ?.filter(option => state.formData.selectedOptions.includes(option.code))
      .map(option => option.label) || [];

  if (isTabletOrMobile) {
    // Mobile/Tablet Version - Horizontal Layout

    return (
      <Box className="infobox-container-mobile" data-testid="infobox">
        <Box className="infobox-flex-container">
          {/* ARIA-Live-Bereich für TalkBack und andere Screenreader */}
          <Box
            aria-live="polite"
            aria-atomic="true"
            role="status"
            sx={{
              position: 'absolute',
              width: '1px',
              height: '1px',
              margin: '-1px',
              border: 0,
              padding: 0,
              clip: 'rect(0 0 0 0)',
              overflow: 'hidden',
              whiteSpace: 'nowrap',
            }}
          >
            Beitrag {state.formData?.versicherungssumme ? '' : 'ab'} {formatCurrency(calculatedPremium)} {paymentPeriod}
          </Box>

          {/* Linker Bereich - Infos */}
          <Box className="infobox-info-section-mobile">
            <Typography variant="body1" component="h2" className="p-bold-700">
              Deine Hardwareversicherung
            </Typography>

            <Box className="infobox-options-container-mobile">
              {/* Zusatzbausteine */}
              <Box className="infobox-section-mobile infobox-section-mobile-addons">
                <Typography variant="caption" component="h3" className="infobox-section-title">
                  Zusatzbausteine
                </Typography>

                {selectedOptions.length > 0 ? (
                  <List dense className="infobox-list-mobile" data-testid="infobox-zusatzbausteine">
                    {selectedOptions.map((option, index) => (
                      <ListItem key={index} disablePadding className="infobox-list-item-mobile">
                        <ListItemIcon className="infobox-list-item-icon-mobile">
                          <CheckIcon className="infobox-list-icon-mobile" />
                        </ListItemIcon>
                        <ListItemText
                          className="infobox-list-item-text-mobile"
                          primary={
                            <Typography variant="caption" className="infobox-list-text-mobile">
                              {option}
                            </Typography>
                          }
                        />
                      </ListItem>
                    ))}
                  </List>
                ) : (
                  <Typography
                    variant="caption"
                    component="p"
                    className="infobox-list-text-mobile is-list-text-disabled"
                  >
                    Keine ausgewählt
                  </Typography>
                )}
              </Box>

              {/* Versicherungssumme */}
              <Box className="infobox-section-mobile infobox-section-mobile-sum">
                <Typography variant="caption" component="h3" className="infobox-section-title">
                  Versicherungssumme
                </Typography>
                <Typography variant="body2" className="infobox-list-text-mobile insurance-sum-text">
                  {versicherungssumme}
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Rechter Bereich - Beitrag */}
          <Box className="infobox-premium-section">
            <Box className="infobox-premium-column-mobile">
              <Typography
                variant="caption"
                component="h3"
                className="infobox-section-title p-bold-700"
                sx={{
                  color: 'var(--color-secondary-text)',
                }}
              >
                Beitrag
              </Typography>

              <Typography
                variant="body1"
                className="infobox-premium-value h2-style"
                sx={{
                  color: 'var(--color-secondary-text)',
                }}
              >
                {state.formData?.versicherungssumme ? '' : 'ab'} {formatCurrency(calculatedPremium)}
              </Typography>

              <Typography
                variant="caption"
                className="infobox-list-text-mobile"
                sx={{
                  color: 'var(--color-secondary-text)',
                }}
              >
                {paymentPeriod}
              </Typography>

              <Typography
                variant="body2"
                component="p"
                className="infobox-list-text-mobile"
                sx={{
                  color: 'var(--color-secondary-text)',
                  paddingTop: '10px',
                }}
              >
                (inkl. 19% VersSt.)
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>
    );
  }

  // Desktop Version - Vertical Layout mit Icon
  return (
    <Box className="infobox-container-desktop" data-testid="infobox">
      <Stack alignItems="center" spacing={0}>
        {/* Hardware Icon */}
        <Avatar className="infobox-avatar">
          <Box
            component="img"
            src={hardwareIcon}
            alt="" //muss leer bleiben für WCAG
            role="presentation"
            className="infobox-icon"
            onError={e => {
              // Fallback wenn Bild nicht gefunden wird
              e.target.style.display = 'none';
            }}
          />
        </Avatar>

        {/* Hauptkarte */}
        <Card className="infobox-card infobox-card-alternate">
          <CardContent className="infobox-card-content">
            {/*  ARIA-Live-Bereich für NVDA und andere Screenreader */}
            <Box
              aria-live="polite"
              aria-atomic="true"
              role="status"
              sx={{
                position: 'absolute',
                width: '1px',
                height: '1px',
                margin: '-1px',
                border: 0,
                padding: 0,
                clip: 'rect(0 0 0 0)',
                overflow: 'hidden',
                whiteSpace: 'nowrap',
              }}
            >
              Beitrag {state.formData?.versicherungssumme ? '' : 'ab'} {formatCurrency(calculatedPremium)}{' '}
              {paymentPeriod}
            </Box>
            <Box className="infobox-center-text">
              <Typography variant="body1" component="h2" className="p-bold-700 infobox-title">
                Deine Hardwareversicherung
              </Typography>
              <Typography variant="body1" component="h3" className=" p-bold-700">
                Versicherungssumme
              </Typography>
              <Typography variant="body2" component="p" data-testid="infobox-versicherungssumme">
                {versicherungssumme}
              </Typography>
            </Box>
          </CardContent>
          <Divider className="infobox-divider" aria-hidden="true" />
          <CardContent className="infobox-card-content-options">
            <Typography variant="body1" component="h2" className="body-text-large-bold">
              Zusatzbausteine
            </Typography>

            {selectedOptions.length > 0 ? (
              <List dense className="infobox-list-desktop" sx={{ mb: 2 }} data-testid="infobox-zusatzbausteine">
                {selectedOptions.map((option, index) => (
                  <ListItem key={index} disablePadding>
                    <ListItemIcon sx={{ minWidth: 24 }}>
                      <CheckIcon sx={{ fontSize: 20, color: 'var(--color-accent-4)', marginRight: '10px' }} />
                    </ListItemIcon>
                    <ListItemText
                      primary={<Typography variant="body2">{option}</Typography>}
                      sx={{
                        marginBottom: '0 !important',
                      }}
                    />
                  </ListItem>
                ))}
              </List>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Keine Zusatzbausteine ausgewählt
              </Typography>
            )}
          </CardContent>
        </Card>

        {/* Beitragskarte */}
        <Card className="infobox-card infobox-card-secondary" sx={{ mt: 0 }}>
          <CardContent sx={{ textAlign: 'center', p: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              <Typography
                variant="body1"
                component="p"
                sx={{ fontWeight: '900 !important', color: 'var(--color-secondary-text)' }}
                data-testid="infobox-gesamtpreis"
              >
                {state.formData?.versicherungssumme ? '' : 'ab'} {formatCurrency(calculatedPremium)}
              </Typography>
              <Typography
                variant="body2"
                component="p"
                sx={{ color: 'var(--color-secondary-text)', marginLeft: '4px' }}
              >
                /{' '}
                <Typography
                  variant="body2"
                  component="span"
                  sx={{ color: 'var(--color-secondary-text)' }}
                  data-testid="infobox-zahlungsintervall"
                >
                  {paymentPeriod}
                </Typography>
              </Typography>
            </Box>
            <Typography variant="caption" component="p" sx={{ color: 'var(--color-secondary-text)' }}>
              (inkl. 19% VersSt.)
            </Typography>
          </CardContent>
        </Card>
      </Stack>
    </Box>
  );
}

export default InfoboxSchutz;
